import { useFormik } from 'formik';
import React, { useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import * as yup from 'yup';
import {
  RewardBannerButtonAction,
  RewardBannerFormValues,
  RewardBannerPrizeType,
} from './types/RewardBannerTypes';
import { NumberField } from '../../../../ui/_fields/NumberField/NumberField';
import { TextareaFieldInput } from '../../../../ui/_fields/TextareaFieldInput/TextareaFieldInput';
import { TextField } from '../../../../ui/_fields/TextField/TextField';
import { FormSubmitRow, UnsavedChangesNotice } from '../../components/UnsavedChangesNotice/UnsavedChangesNotice';
import { useReportDirty } from '../../hooks/useReportDirty';
import { RewardBannerButtonTargetInput } from './components/RewardBannerButtonTargetInput';
import { RewardBannerCoverInput } from './components/RewardBannerCoverInput/RewardBannerCoverInput';
import { Variant } from '../../../../../dorian-shared/components/ImageCropper/ImageCropper';
import { BadgeWithTooltip } from '../../../../ui/AvatarTool/BadgeWithTooltip/BadgeWithTooltip';
import { ConfirmModal } from '../../../../ui/ConfirmModal/ConfirmModal';
import { ImageInput } from '../../../Events/components/ImageInput/ImageInput';
import { RingColorPicker } from '../../../Events/components/RingColorPicker/RingColorPicker';

// eslint-disable-next-line no-template-curly-in-string
const requiredMessage = '${path} is required';
// eslint-disable-next-line no-template-curly-in-string
const maxMessage = '${path} should be up to ${max} characters';
// eslint-disable-next-line no-template-curly-in-string
const minMessage = '${path} should be at least ${min} characters';

const titleMaxLength = 50;
const descriptionMaxLength = 10000;
const labelMaxLength = 40;
const buttonTextMaxLength = 20;
const specialRequirementsMaxLength = 10000;
const maxPrizeValueLength = 50;
const ringColorRegex = /^(#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}))?$/;

const CARD_ICON_INFO = {
  title: 'Card Icon',
  content: 'Shows as an oval on the shelf. Sides and corners are clipped, so keep the subject in the middle. If left empty, the landscape banner gets cropped into that oval and usually looks wrong.',
} as const;

const MODAL_COVER_INFO = {
  title: 'Modal Cover',
  content: 'This is the whole popup background. The app draws nothing over it, so put any text in the image itself. If left empty, the popup shows the landscape banner, cropped to fill the tall frame.',
} as const;

function buildSchema(promotionStoryUuid: string | null) {
  return yup.object().shape({
  title: yup.string().label('Title').max(titleMaxLength, maxMessage).required(requiredMessage),
  description: yup.string().label('Description').min(10, minMessage).max(descriptionMaxLength, maxMessage)
    .required(requiredMessage),
  label: yup.string().label('Label').max(labelMaxLength, maxMessage),
  imageUrl: yup.string().label('Image').required(requiredMessage),
  cardImageUrl: yup.string().label('Card Image'),
  detailImageUrl: yup.string().label('Modal Cover Image'),
  ringColor: yup.string().label('Ring Colour').matches(ringColorRegex, 'Ring colour must be #RGB or #RRGGBB'),
  specialRequirements: yup.string().label('Rules').min(10, minMessage).max(specialRequirementsMaxLength, maxMessage),
  active: yup.bool(),
  priority: yup.number().label('Order').integer('Only whole numbers are allowed').min(1, minMessage)
    .required(requiredMessage),
  buttonText: yup.string().label('Button Text').max(buttonTextMaxLength, maxMessage).required(requiredMessage),
  buttonAction: yup.string().required(),
  buttonTarget: yup.string().when('buttonAction', {
    is: RewardBannerButtonAction.Story,
    then: (field) => field.test(
      'promotion-story-uuid',
      'No story UUID configured on this promotion',
      () => Boolean(promotionStoryUuid?.trim()),
    ),
    otherwise: (field) => field.label('Button Target').required(requiredMessage),
  }),
  prize: yup.object().shape({
    prizeType: yup.string(),
    prizeValue: yup.string(),
  }).test(
    'has-prize-value-if-hearts',
    "Prize value can't be empty if prize type selected",
    (prize) => (prize.prizeType === RewardBannerPrizeType.Hearts
      || prize.prizeType === RewardBannerPrizeType.Sapphires
      ? Boolean(prize.prizeValue)
      : true),
  ).test(
    'prize-value-is-too-long',
    `Prize Value should be up to ${maxPrizeValueLength}`,
    (prize) => {
      const length = prize.prizeValue?.length ?? 0;
      return length <= maxPrizeValueLength;
    },
  ),
  });
}

interface RewardBannerFormProps {
  formValues: RewardBannerFormValues;
  isLoading: boolean;
  onSubmit: (values: RewardBannerFormValues) => void;
  submitButtonText: string;
  disabled?: boolean;
  promotionStoryUuid: string | null;
  onDirtyChange?: (dirty: boolean) => void;
}

export function RewardBannerForm(props: RewardBannerFormProps) {
  const {
    formValues,
    isLoading,
    onSubmit,
    submitButtonText,
    disabled = false,
    promotionStoryUuid,
    onDirtyChange,
  } = props;

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
    dirty,
  } = useFormik<RewardBannerFormValues>({
    enableReinitialize: true,
    validationSchema: buildSchema(promotionStoryUuid),
    initialValues: formValues,
    onSubmit: (submittedValues) => {
      onSubmit(submittedValues);
    },
  });

  useReportDirty(dirty, onDirtyChange);
  const [pendingRemoval, setPendingRemoval] = useState<'card' | 'modal' | null>(null);

  const handleImageChange = (fileInputValue: RewardBannerFormValues['newCoverImage'], dataURL: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      imageUrl: dataURL,
      newCoverImage: fileInputValue,
    }));
  };

  const handleCardImageChange = (fileInputValue: RewardBannerFormValues['newCardImage'], dataURL: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      cardImageUrl: dataURL,
      newCardImage: fileInputValue,
      // The API rejects a file and a removal flag for the same field.
      removeCardImage: false,
    }));
  };

  const handleModalImageChange = (fileInputValue: RewardBannerFormValues['newModalImage'], dataURL: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      detailImageUrl: dataURL,
      newModalImage: fileInputValue,
      removeModalImage: false,
    }));
  };

  const handleRemovalConfirm = () => {
    if (pendingRemoval === 'card') {
      setValues((prevValues) => ({
        ...prevValues,
        cardImageUrl: '',
        newCardImage: undefined,
        removeCardImage: true,
      }));
    } else if (pendingRemoval === 'modal') {
      setValues((prevValues) => ({
        ...prevValues,
        detailImageUrl: '',
        newModalImage: undefined,
        removeModalImage: true,
      }));
    }
    setPendingRemoval(null);
  };

  const handleRingColorChange = (ringColor: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      ringColor,
      ringColorTouched: true,
    }));
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <fieldset disabled={disabled}>
      <Row className="mb-3">
        <Col md={12}>
          <TextField
            label="Event Title"
            name="title"
            placeholder="COMPETITION STARTS!"
            value={values.title}
            onChange={handleChange}
            error={touched.title ? errors.title : ''}
            maxLength={titleMaxLength}
          />
        </Col>
        <Col md={12}>
          <TextareaFieldInput
            rows={3}
            label="Event Description"
            placeholder="Our Vampire Insiders will already be fans of Anne..."
            name="description"
            value={values.description}
            onChange={handleChange}
            error={touched.description ? errors.description : ''}
            maxLength={descriptionMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <TextField
            label="Event Label"
            name="label"
            placeholder="Label to display on event card (for example FEATURED)"
            value={values.label}
            onChange={handleChange}
            error={touched.label ? errors.label : ''}
            maxLength={labelMaxLength}
          />
        </Col>
        <Col md={6}>
          <NumberField
            label="Order (bigger number - higher in the list)"
            name="priority"
            placeholder="order of events - events with bigger value shows first"
            min="1"
            step="1"
            value={values.priority}
            onChange={handleChange}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <TextField
            label="Button Text"
            name="buttonText"
            placeholder="Text to be displayed inside CTA button"
            value={values.buttonText}
            onChange={handleChange}
            error={touched.buttonText ? errors.buttonText : ''}
            maxLength={buttonTextMaxLength}
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Rules"
            name="specialRequirements"
            placeholder="Rules of event"
            value={values.specialRequirements}
            onChange={handleChange}
            error={touched.specialRequirements ? errors.specialRequirements : ''}
            maxLength={specialRequirementsMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Button type</Form.Label>
            <Form.Control
              value={values.buttonAction}
              as="select"
              required
              name="buttonAction"
              onChange={handleChange}
              isInvalid={!!errors.buttonAction}
            >
              <option value={RewardBannerButtonAction.Story}>Story</option>
              <option value={RewardBannerButtonAction.Chat}>Chat</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <RewardBannerButtonTargetInput
            buttonAction={values.buttonAction}
            value={values.buttonTarget}
            onChange={(buttonTarget: string) => setFieldValue('buttonTarget', buttonTarget)}
            error={touched.buttonTarget ? errors.buttonTarget : ''}
            promotionStoryUuid={promotionStoryUuid}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Cover Prize Type</Form.Label>
            <Form.Control
              value={values.prize?.prizeType ?? ''}
              as="select"
              name="prize.prizeType"
              onChange={handleChange}
            >
              {Object.entries(RewardBannerPrizeType)
                .map(([key, value]) => (
                  <option key={key} value={value}>
                    {value !== RewardBannerPrizeType.None ? value : 'None'}
                  </option>
                ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <TextField
            label="Cover Prize value"
            name="prize.prizeValue"
            placeholder="Prize value - can be any text"
            value={values.prize?.prizeValue ?? ''}
            onChange={handleChange}
            error={touched.prize ? (errors.prize as string) : ''}
            maxLength={maxPrizeValueLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Form.Group as={Col} md="4">
          <Form.Check
            label="Active"
            name="active"
            checked={values.active}
            onChange={handleChange}
          />
        </Form.Group>
      </Row>

      <Card className="my-4 border-secondary">
        <Card.Header className="d-flex align-items-center gap-2 py-2 bg-light">
          <strong className="text-dark">Event Shelf</strong>
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>
            (optional) — how this event looks on the shelf and in the detail popup
          </span>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <ImageInput
                label="Card Icon"
                labelBadge={(
                  <BadgeWithTooltip
                    id="info-card-icon"
                    title={CARD_ICON_INFO.title}
                    content={CARD_ICON_INFO.content}
                    placement="right"
                  />
                )}
                initialURL={values.cardImageUrl}
                onConfirm={handleCardImageChange}
                error={touched.cardImageUrl ? errors.cardImageUrl : ''}
                imageCropperProps={{
                  aspectRatio: 21 / 23.5,
                  variant: Variant.circle,
                }}
                getBlobOptions={{
                  croppedImageType: 'image/jpeg',
                  maxWidth: 1024,
                  maxHeight: 1152,
                  imageSmoothingQuality: 'high',
                }}
                previewVariant="oval"
                onRemoveRequest={values.cardImageUrl && !values.newCardImage
                  ? () => setPendingRemoval('card')
                  : undefined}
                accept="image/*"
              />
            </Col>
            <Col md={6}>
              <ImageInput
                label="Modal Cover"
                labelBadge={(
                  <BadgeWithTooltip
                    id="info-modal-cover"
                    title={MODAL_COVER_INFO.title}
                    content={MODAL_COVER_INFO.content}
                    placement="right"
                  />
                )}
                initialURL={values.detailImageUrl}
                onConfirm={handleModalImageChange}
                error={touched.detailImageUrl ? errors.detailImageUrl : ''}
                imageCropperProps={{
                  aspectRatio: 1080 / 1920,
                }}
                getBlobOptions={{
                  croppedImageType: 'image/jpeg',
                  maxWidth: 2250,
                  maxHeight: 4000,
                  imageSmoothingQuality: 'high',
                }}
                previewVariant="compact"
                onRemoveRequest={values.detailImageUrl && !values.newModalImage
                  ? () => setPendingRemoval('modal')
                  : undefined}
                accept="image/*"
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <RingColorPicker
                value={values.ringColor}
                onChange={handleRingColorChange}
                error={errors.ringColor}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="mb-3">
        <Col md={6}>
          <RewardBannerCoverInput
            initialURL={values.imageUrl}
            onConfirm={handleImageChange}
            error={touched.imageUrl ? errors.imageUrl : ''}
            disabled={disabled}
            imageCropperProps={{
              aspectRatio: 21 / 10,
            }}
            getBlobOptions={{
              croppedImageType: 'image/jpeg',
              maxWidth: 3220,
              maxHeight: 1534,
              imageSmoothingQuality: 'high',
            }}
            accept="image/*"
          />
        </Col>
      </Row>
      </fieldset>

      <ConfirmModal
        show={pendingRemoval !== null}
        centered
        title={pendingRemoval === 'card' ? 'Remove card icon?' : 'Remove modal cover?'}
        confirmButtonTitle="Remove image"
        cancelButtonTitle="Keep image"
        onConfirm={handleRemovalConfirm}
        onCancel={() => setPendingRemoval(null)}
        onHide={() => setPendingRemoval(null)}
        isLoading={false}
      >
        {pendingRemoval === 'card' ? (
          <>
            <p>
              Removing the card icon is permanent and takes effect when you save.
              Without a card icon, the shelf will crop your existing landscape banner
              into an oval shape.
            </p>
            <p><strong>This cannot be undone.</strong></p>
          </>
        ) : (
          <>
            <p>
              Removing the modal cover is permanent and takes effect when you save.
              Without a modal cover, the popup will show that same banner, cropped to fill the
              tall frame.
            </p>
            <p><strong>This cannot be undone.</strong></p>
          </>
        )}
      </ConfirmModal>

      {!disabled && (
        <FormSubmitRow>
          <Button type="submit" disabled={isLoading || !dirty}>
            {isLoading && (
              <Spinner
                size="sm"
                animation="border"
              />
            )}
            {submitButtonText}
          </Button>
          <UnsavedChangesNotice show={dirty} />
        </FormSubmitRow>
      )}
    </Form>
  );
}
